import { observer } from "mobx-react";
import React, { useContext } from "react";
import OrderSettingsPageStoreContext from "../context";
import { FormControl, Paper, Typography } from "../../../../../common/components/libs";
import { FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch } from "@material-ui/core";
import { changeEventHandlerDecorator } from "../../../../../common/libs";
import { TimePicker } from "@material-ui/pickers";
import { getHours } from "date-fns/esm";
import { getMinutes } from "date-fns";
import { WorkingScheduleDay, WorkingScheduleDaySlot } from "../../../../../services/menu/models";
import { useTranslation } from "react-i18next";
import moment from 'moment'
import tz from 'moment-timezone'

const timeZonePeriod =
    [-720, -690, -660, -630, -600, -570, -540, -510, -480,
        -450, -420, -390, -360, -330, -300, -270, -240, -210,
        -180, -150, -120, -90, -60, -30, 0, +30, +60, +90,
        +120, +150, +180, +210, +240, +270, +300, +330, +360,
        +390, +420, +450, +480, +510, +540, +570, +600, 630,
        660, +690, +720
    ];

const formatTimezone = (zone: number) => {
    return zone < 0 ? zone/60 : "+" + zone/60;
}


interface WorkingSlotProps{
    item: WorkingScheduleDaySlot,
    daySchedule: WorkingScheduleDay,
    updateValue: (t: WorkingScheduleDay) => void;
}

const WorkingSlot = observer((props : WorkingSlotProps) => {
    const {t} = useTranslation(["general", "menu"]);
    const {item, daySchedule} = props;


    const dateFrom = item.fromHour * 60 + item.fromMinute - moment().utcOffset();
    const valueDateFrom = new Date(dateFrom * 60 * 1000);
    const dateTo = item.toHour * 60 + item.toMinute - moment().utcOffset();
    const valueDateTo = new Date(dateTo * 60 * 1000);

    return (
        <div style={{display:"flex", alignItems:"center"}}>
            <TimePicker
                label={t("general:from")}
                ampm={false}
                disabled={daySchedule.isDayOff}
                onChange={function (date: Date): void {
                    props.updateValue({
                        ...daySchedule,
                        slots:[
                            {
                                fromHour: getHours(date),
                                fromMinute:getMinutes(date),
                                toHour: item.toHour,
                                toMinute: item.toMinute
                            }
                        ]});
                } }
                value={valueDateFrom}
            />


            <TimePicker
                ampm={false}
                label={t("general:to")}
                disabled={daySchedule.isDayOff}
                onChange={function (date: Date): void {
                    props.updateValue({
                        ...daySchedule,
                        slots:[
                            {
                                fromHour: item.fromHour,
                                fromMinute:item.fromMinute,
                                toHour:  getHours(date),
                                toMinute: getMinutes(date)
                            }
                        ]});
                } }
                value={valueDateTo}
            />
        </div>
    )
})


const WorkingScheduleBlock = () => {
    const {t} = useTranslation(["general", "menu"]);
    const store = useContext(OrderSettingsPageStoreContext);


    return <>
        <Typography variant={"h3"} mt={20}>
            {t("menu:orderSettingsPage.workingHours")}
        </Typography>
        <Typography variant={"body1"} color={'textSecondary'} mb={2}>
            {t("menu:orderSettingsPage.tellAboutSchedule")}
        </Typography>


        <Paper p={5} >
            <Typography variant={"h6"} >
                {t("menu:orderSettingsPage.timezone")}
            </Typography>
            <FormControl fullWidth margin={"normal"}>
                <InputLabel htmlFor="timeZone">{t("menu:orderSettingsPage.timezone")}</InputLabel>
                <Select
                    value={store.workingSchedule.timezoneInMinutes}
                    onChange={changeEventHandlerDecorator(store.setTimezone.bind(store))}
                >
                    {timeZonePeriod.map(item => {
                        return (
                            <MenuItem value={item}> {formatTimezone(item)} ч.</MenuItem>
                        )
                    })
                    }
                </Select>
            </FormControl>
        </Paper>


        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"} >
                        {t("menu:orderSettingsPage.monday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.monday.isDayOff}
                                    onChange={(event) => {
                                        store.setMondaySchedule({
                                            ...store.workingSchedule.monday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.monday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            {
                store.workingSchedule.monday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.monday}
                        updateValue={store.setMondaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"}>
                        {t("menu:orderSettingsPage.tuesday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.tuesday.isDayOff}
                                    onChange={(event) => {
                                        store.setTuesdaySchedule({
                                            ...store.workingSchedule.tuesday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.tuesday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            {
                store.workingSchedule.tuesday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.tuesday}
                        updateValue={store.setTuesdaySchedule.bind(store)}
                    />
                })
            }
        </Paper>


        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"} >
                        {t("menu:orderSettingsPage.wednesday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.wednesday.isDayOff}
                                    onChange={(event) => {
                                        store.setWednesdaySchedule({
                                            ...store.workingSchedule.wednesday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.wednesday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>



            {
                store.workingSchedule.wednesday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.wednesday}
                        updateValue={store.setWednesdaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"}>
                        {t("menu:orderSettingsPage.thursday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.thursday.isDayOff}
                                    onChange={(event) => {
                                        store.setThursdaySchedule({
                                            ...store.workingSchedule.thursday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.thursday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>


            {
                store.workingSchedule.thursday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.thursday}
                        updateValue={store.setThursdaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"}>
                        {t("menu:orderSettingsPage.friday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.friday.isDayOff}
                                    onChange={(event) => {
                                        store.setFridaySchedule({
                                            ...store.workingSchedule.friday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.friday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            {
                store.workingSchedule.friday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.friday}
                        updateValue={store.setFridaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"}>
                        {t("menu:orderSettingsPage.saturday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.saturday.isDayOff}
                                    onChange={(event) => {
                                        store.setSaturdaySchedule({
                                            ...store.workingSchedule.saturday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.saturday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>

            {
                store.workingSchedule.saturday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.saturday}
                        updateValue={store.setSaturdaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

        <Paper p={5} mt={2}>
            <Grid container alignItems={'center'}>
                <Grid item xs={12} sm={10} md={10} >
                    <Typography variant={"h6"}>
                        {t("menu:orderSettingsPage.sunday")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!store.workingSchedule.sunday.isDayOff}
                                    onChange={(event) => {
                                        store.setSundaySchedule({
                                            ...store.workingSchedule.sunday,
                                            isDayOff: !event.target.checked
                                        });
                                    }}
                                    value={!store.workingSchedule.sunday.isDayOff}
                                />
                            }
                            label={t("menu:orderSettingsPage.work")}
                        />
                    </FormControl>
                </Grid>
            </Grid>


            {
                store.workingSchedule.sunday.slots.map(item => {
                    return <WorkingSlot
                        item={item}
                        daySchedule={store.workingSchedule.sunday}
                        updateValue={store.setSundaySchedule.bind(store)}
                    />
                })
            }
        </Paper>

    </>

}


export default observer(WorkingScheduleBlock)
